import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  Box,
  Button,
  Container,
  LinearProgress,
  Typography,
} from "@mui/material";
import { PageTitleBar } from "common/components";
import { useTranslation } from "common/locales";
import {
  addFilledOut,
  ProgrammeActions,
  selectAllProgrammeActions,
  setQuickAssessmentFinished,
  setQuickAssessmentStarted,
  useAppDispatch,
  useAppSelector,
} from "model";
import {
  fetchAllQuickAssessmentResults,
  selectQuickAssessmentResultsById,
} from "model/quickAssessmentSlice";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import StepCard from "./FirstStepCard";
import NavigationLock from "./NavigationLock";
import { NO_PROGRAMM_ID } from "./Programmes";

const REFRESH_INTERVAL = 1 * 1000;

export default function ProgrammesList() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.details);
  const { programmeId } = useParams<{ programmeId: string }>();
  const [expandedProgrammes, setExpandedProgrammes] = useState<Set<string>>(
    new Set()
  );
  const [truncatedProgrammes, setTruncatedProgrammes] = useState<Set<string>>(
    new Set()
  );
  const textRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  const programmesActions = useAppSelector((state) =>
    selectAllProgrammeActions(state)
  );

  const sortedProgrammesActions = programmesActions.sort(
    (a: ProgrammeActions, b: ProgrammeActions) => {
      return a.programmeName > b.programmeName ? 1 : -1;
    }
  );

  const loading = useAppSelector(
    (state) => state.programmeActions.loading === "pending"
  );

  const toggleExpanded = (programmeGuid: string) => {
    setExpandedProgrammes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(programmeGuid)) {
        newSet.delete(programmeGuid);
      } else {
        newSet.add(programmeGuid);
      }
      return newSet;
    });
  };

  // Check if text is truncated
  useEffect(() => {
    const newTruncated = new Set<string>();
    sortedProgrammesActions.forEach((programme) => {
      const element = textRefs.current[programme.guid];
      if (element && element.scrollHeight > element.clientHeight) {
        newTruncated.add(programme.guid);
      }
    });
    setTruncatedProgrammes(newTruncated);
  }, [sortedProgrammesActions]);

  const quickAssessmentStarted = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).quickAssessmentStarted
  );

  const quickAssessmentResults = useAppSelector((state) =>
    selectQuickAssessmentResultsById(state, NO_PROGRAMM_ID)
  );

  const hasFilledOutQuickAssessment = !(
    Object.keys(quickAssessmentResults?.results ?? {}).length === 0
  );

  const programmeActionsQuestionnaireFilledOutIds = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).programmeActionsQuestionnaireFilledOutIds
  );

  const filledOutButResultNotAvailableYet =
    programmeActionsQuestionnaireFilledOutIds.programmeActionsQuestionnaireFilledOutIds.includes(
      NO_PROGRAMM_ID
    ) && !hasFilledOutQuickAssessment;

  const loadingQuickAssessment = useAppSelector(
    (state) => state.quickAssessmentResults.loading === "pending"
  );

  // Handle form submission message from iframe
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleMessages = (content: any) => {
      if (!content) {
        return;
      }
      // Check if submission is completed.
      if (
        typeof content.data === "object" &&
        content.data.action === "submission-completed"
      ) {
        dispatch(addFilledOut({ filledOutProgrammeId: NO_PROGRAMM_ID }));
        dispatch(setQuickAssessmentFinished(false));
        return;
      }
    };

    if (window.addEventListener) {
      window.addEventListener("message", handleMessages, true); // Capture
    }
    return () => {
      if (window.removeEventListener) {
        window.removeEventListener("message", handleMessages, true);
      }
    };
  }, [dispatch, programmeId]);

  // Listen to route changes and cleanup quickAssessmentStarted state when navigating away
  useEffect(() => {
    const unsetQuickAssessmentStarted = history.listen((e) => {
      if (e.pathname !== "/programmes") {
        // User is navigating away from programmes page
        dispatch(setQuickAssessmentFinished(false));
      }
    });

    return function cleanup() {
      unsetQuickAssessmentStarted();
    };
  }, [dispatch, history]);

  // Prevent accidental page close/refresh when questionnaire is in progress
  useEffect(() => {
    const onBeforeUnload = (ev: Event) => {
      if (quickAssessmentStarted.quickAssessmentStarted) {
        ev.preventDefault();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickAssessmentStarted.quickAssessmentStarted]);

  // Poll for quick assessment results after form submission
  useEffect(() => {
    if (filledOutButResultNotAvailableYet) {
      const reloadQuickAssessmentResults = async () => {
        if (!loadingQuickAssessment) {
          await dispatch(fetchAllQuickAssessmentResults());
        }
        return;
      };
      const refreshInterval = setInterval(
        reloadQuickAssessmentResults,
        REFRESH_INTERVAL
      );
      // Clear interval when results are available
      if (hasFilledOutQuickAssessment) {
        clearInterval(refreshInterval);
      }
      return () => clearInterval(refreshInterval);
    }
  }, [
    dispatch,
    filledOutButResultNotAvailableYet,
    loadingQuickAssessment,
    hasFilledOutQuickAssessment,
  ]);

  // Show loading indicator while waiting for results
  if (loading || loadingQuickAssessment || filledOutButResultNotAvailableYet) {
    return <LinearProgress />;
  }

  // Render content section (programmes list or no-programmes message)
  const renderContent = () => {
    // Show "no programmes" content when there are no programmes
    if (sortedProgrammesActions.length === 0) {
      const maxHeight = "auto";

      if (!hasFilledOutQuickAssessment) {
        return (
          <Box
            sx={{
              backgroundColor: "white",
              p: 3,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                maxWidth: "600px",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 5,
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontSize: "28px",
                  fontWeight: 600,
                  mb: 3,
                  color: "#000",
                  textAlign: "center",
                }}
              >
                Join a programme to get the best out of ruumi!
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "#666",
                  lineHeight: 1.5,
                  textAlign: "left",
                  mb: 3,
                }}
              >
                {t("no.programmes.available.body.web")}
              </Typography>

              {!quickAssessmentStarted.quickAssessmentStarted &&
                !hasFilledOutQuickAssessment && (
                  <StepCard
                    stepNumber={1}
                    stepTitle="Do a quick assessment"
                    stepDescription="The first step is to tell us a bit about your farm."
                    buttonText="TELL US ABOUT YOUR FARM"
                    daysLeft={122}
                    status="In progress"
                    onButtonClick={() => {
                      dispatch(setQuickAssessmentStarted(true));
                    }}
                  />
                )}
            </Box>
          </Box>
        );
      } else {
        return (
          <Box
            sx={{
              backgroundColor: "white",
              p: 3,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "calc(100vh - 100px)",
            }}
          >
            <Box sx={{ maxWidth: "600px", width: "100%" }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontSize: "28px",
                  fontWeight: 600,
                  mb: 3,
                  color: "#000",
                  textAlign: "center",
                }}
              >
                Thank you for taking the time to tell us more about your farm!
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "#666",
                  lineHeight: 1.5,
                  textAlign: "left",
                }}
              >
                {t("quick.assessment.thank.you.web")}
              </Typography>
            </Box>
          </Box>
        );
      }
    }

    // Show programmes list
    return (
      <Box>
        <PageTitleBar title={t("programmes.toolbar.title")} showDivider />
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            bgcolor: "white",
            padding: 3,
          }}
        >
          {sortedProgrammesActions.map((programme) => {
            return (
              <Box
                key={programme.guid}
                sx={{
                  bgcolor: "white",
                  borderRadius: "8px",
                  border: "1px solid #e4e4e7",
                  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                  mb: 3,
                  overflow: "hidden",
                }}
              >
                {/* Banner Image */}
                <Box
                  sx={{
                    height: "120px",
                    ...(programme.guid === "6vczLKixKSwPkIFP3JkkGD"
                      ? {
                          backgroundImage:
                            'url("https://ruumi-dashboard-assets.ruumi.io/lidl-sustainable-beef-banner.png")',
                          backgroundSize: "contain",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                          backgroundColor: "#395499",
                        }
                      : programme.guid === "3UqQUHVbKY5CMBthVL5Ltq"
                      ? {
                          backgroundImage:
                            'url("https://ruumi-dashboard-assets.ruumi.io/Pattemores.png")',
                          backgroundSize: "contain",
                          backgroundPosition: "center",
                          backgroundRepeat: "repeat",
                          backgroundColor: "#ffffffff",
                        }
                      : programme.guid === "5OPgsM2LDKgFHqhLO4Kzpv"
                      ? {
                          backgroundImage:
                            'url("https://ruumi-dashboard-assets.ruumi.io/County-Milk-2.png")',
                          backgroundSize: "contain",
                          backgroundPosition: "center",
                          backgroundRepeat: "repeat",
                          backgroundColor: "#ffffffff",
                        }
                      : {
                          backgroundColor: "#1C4321",
                        }),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {programme.guid !== "6vczLKixKSwPkIFP3JkkGD" &&
                    programme.guid !== "3UqQUHVbKY5CMBthVL5Ltq" &&
                    programme.guid !== "5OPgsM2LDKgFHqhLO4Kzpv" && (
                      <Typography
                        variant="h5"
                        sx={{
                          color: "white",
                          fontWeight: 600,
                          textAlign: "center",
                          px: 2,
                        }}
                      >
                        {programme.programmeName}
                      </Typography>
                    )}
                </Box>

                {/* Card Content */}
                <Box sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontSize: "20px",
                        fontWeight: 600,
                        color: "#000",
                        flex: 1,
                      }}
                    >
                      {programme.programmeName}
                    </Typography>

                    <Button
                      variant="contained"
                      onClick={() => {
                        history.push(`/programme-details/${programme.guid}`);
                      }}
                      sx={{
                        bgcolor: "#1C4321",
                        color: "white",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        fontSize: "12px",
                        px: 3,
                        py: 1,
                        "&:hover": {
                          bgcolor: "#2E7831",
                        },
                        ml: 2,
                        flexShrink: 0,
                      }}
                    >
                      {t("programmes.viewProgramme")}
                    </Button>
                  </Box>

                  {/* Welcome Text Preview */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      ref={(el) => (textRefs.current[programme.guid] = el)}
                      variant="body2"
                      sx={{
                        color: "#666",
                        lineHeight: 1.5,
                        ...(!expandedProgrammes.has(programme.guid) && {
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }),
                      }}
                    >
                      {programme.welcomeText?.replace(
                        "FARMER_NAME",
                        user?.name ?? ""
                      ) ?? ""}
                    </Typography>
                    {truncatedProgrammes.has(programme.guid) && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(programme.guid);
                        }}
                        sx={{
                          textTransform: "none",
                          color: "#4CAF50",
                          fontWeight: 500,
                          fontSize: "14px",
                          p: 0,
                          mt: 0.5,
                          minWidth: "auto",
                          "&:hover": {
                            backgroundColor: "transparent",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {expandedProgrammes.has(programme.guid)
                          ? "View less"
                          : "View more"}
                      </Button>
                    )}
                  </Box>

                  {/* Status Indicator */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: "20px", color: "#666" }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontWeight: 500,
                      }}
                    >
                      {t("programmes.inProgress")}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Container>
      </Box>
    );
  };

  // Render iframe section when quick assessment is started
  const renderIframe = () => {
    if (!quickAssessmentStarted.quickAssessmentStarted) {
      return null;
    }

    return (
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: 0,
          boxShadow: 0,
          padding: 0,
          margin: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "row",
          height: "100vh",
        }}
      >
        <iframe
          frameBorder="0"
          id="JotFormIFrame-251602311826044"
          title="New Customer Registration Form"
          allow="geolocation; microphone; camera; fullscreen; payment"
          src={`https://form.jotform.com/251602311826044?email=${
            user?.mail ?? ""
          }&guid=${user?.guid ?? ""}`}
          style={{
            flex: 1,
            padding: 0,
            margin: 0,
            height: "100%",
            width: "100%",
            border: "none",
          }}
        ></iframe>
        <script src="https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js"></script>
        <script>
          window.jotformEmbedHandler("iframe[id='JotFormIFrame-251602311826044']",
          "https://form.jotform.com/")
        </script>
      </Box>
    );
  };

  return (
    <Box>
      <NavigationLock when={quickAssessmentStarted.quickAssessmentStarted} />
      {quickAssessmentStarted.quickAssessmentStarted
        ? renderIframe()
        : renderContent()}
    </Box>
  );
}
